import { useCallback, useEffect, useRef, useState, type DragEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { evaluateKeyframes } from '../../core/keyframes';
import type { Layer } from '../../core/schema';
import { renderComposition } from '../../services/compositionRenderer';
import { useEditorStore } from '../../store/editorStore';
import { usePlaybackStore } from '../../store/playbackStore';

function layerBounds(layer: Layer, width: number, height: number, projectWidth: number, projectHeight: number) {
  let objectWidth = projectWidth;
  let objectHeight = projectHeight;
  if (layer.type === 'text' || layer.type === 'caption') {
    objectWidth = Math.min(projectWidth * 0.82, Math.max(260, (layer.text?.length ?? 8) * (layer.fontSize ?? 64) * 0.62));
    objectHeight = (layer.fontSize ?? 64) * 1.35;
  } else if (layer.type === 'scene3d') {
    objectWidth = projectWidth;
    objectHeight = projectHeight;
  }
  const scaleX = width / projectWidth;
  const scaleY = height / projectHeight;
  const position = layer.transform.position;
  return {
    left: width / 2 + position[0] * scaleX - objectWidth * layer.transform.scale[0] * scaleX / 2,
    top: height / 2 + position[1] * scaleY - objectHeight * layer.transform.scale[1] * scaleY / 2,
    width: Math.abs(objectWidth * layer.transform.scale[0] * scaleX),
    height: Math.abs(objectHeight * layer.transform.scale[1] * scaleY),
    rotation: layer.transform.rotation[2]
  };
}

export function PreviewCanvas({ safeZones, grid }: { safeZones: boolean; grid: boolean }) {
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [draggingFiles, setDraggingFiles] = useState(false);
  const project = useEditorStore((state) => state.project);
  const selectedIds = useEditorStore((state) => state.selectedLayerIds);
  const selectLayer = useEditorStore((state) => state.selectLayer);
  const updateLayer = useEditorStore((state) => state.updateLayer);
  const beginTransaction = useEditorStore((state) => state.beginTransaction);
  const endTransaction = useEditorStore((state) => state.endTransaction);
  const importFiles = useEditorStore((state) => state.importFiles);
  const addAsset = useEditorStore((state) => state.addAssetToTimeline);
  const composition = project.compositions[project.activeCompositionId];
  const selected = selectedIds.length === 1 ? project.layers[selectedIds[0]] : null;
  const renderPending = useRef(false);
  const rerender = useRef(false);

  const draw = useCallback(() => {
    if (!canvas.current) return;
    if (renderPending.current) { rerender.current = true; return; }
    renderPending.current = true;
    const state = usePlaybackStore.getState();
    const editor = useEditorStore.getState();
    const quality = state.playing && (editor.project.compositions[editor.project.activeCompositionId].width > 1920) ? 0.5 : 1;
    void renderComposition(canvas.current, editor.project, state.currentTime, state.playing, quality).finally(() => {
      renderPending.current = false;
      if (rerender.current) { rerender.current = false; draw(); }
    });
  }, []);

  useEffect(() => {
    draw();
    const unsubPlayback = usePlaybackStore.subscribe(draw);
    const unsubEditor = useEditorStore.subscribe((state, previous) => { if (state.project !== previous.project) draw(); });
    return () => { unsubPlayback(); unsubEditor(); };
  }, [draw]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const update = () => {
      const rect = element.getBoundingClientRect();
      const availableWidth = Math.max(1, rect.width - 72);
      const availableHeight = Math.max(1, rect.height - 72);
      const ratio = composition.width / composition.height;
      let width = Math.min(availableWidth, availableHeight * ratio);
      let height = width / ratio;
      if (height > availableHeight) { height = availableHeight; width = height * ratio; }
      setDisplaySize({ width, height });
    };
    const observer = new ResizeObserver(update);
    observer.observe(element);
    update();
    return () => observer.disconnect();
  }, [composition.width, composition.height]);

  const onPointerDown = (event: ReactPointerEvent, layer: Layer) => {
    if (layer.locked) return;
    event.preventDefault();
    event.stopPropagation();
    selectLayer(layer.id);
    beginTransaction();
    const start = { x: event.clientX, y: event.clientY, position: [...layer.transform.position] as [number, number, number] };
    event.currentTarget.setPointerCapture(event.pointerId);
    const move = (moveEvent: PointerEvent) => {
      const dx = (moveEvent.clientX - start.x) * composition.width / displaySize.width;
      const dy = (moveEvent.clientY - start.y) * composition.height / displaySize.height;
      updateLayer(layer.id, (draft) => { draft.transform.position = [start.position[0] + dx, start.position[1] + dy, start.position[2]]; }, false);
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); endTransaction(); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDraggingFiles(false);
    const assetId = event.dataTransfer.getData('application/x-open-motion-asset');
    if (assetId) { addAsset(assetId); return; }
    const files = [...event.dataTransfer.files];
    if (files.length) void importFiles(files, true);
  };

  const bounds = selected ? layerBounds(selected, displaySize.width, displaySize.height, composition.width, composition.height) : null;
  const localTime = selected ? usePlaybackStore.getState().currentTime - selected.start : 0;
  const animatedPosition = selected ? evaluateKeyframes(selected.keyframes['transform.position'], localTime, selected.transform.position) as [number, number, number] : null;
  if (selected && bounds && animatedPosition) {
    bounds.left += (animatedPosition[0] - selected.transform.position[0]) * displaySize.width / composition.width;
    bounds.top += (animatedPosition[1] - selected.transform.position[1]) * displaySize.height / composition.height;
  }

  return <div ref={container} className={`preview-stage ${draggingFiles ? 'is-dragging' : ''}`} onClick={() => selectLayer(null)} onDragEnter={(event) => { event.preventDefault(); setDraggingFiles(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDraggingFiles(false); }} onDrop={onDrop}>
    <div className="canvas-shell" style={{ width: displaySize.width, height: displaySize.height }}>
      <canvas ref={canvas} className="composition-canvas" />
      {grid && <div className="canvas-grid" />}
      {safeZones && <><div className="safe-zone safe-action" /><div className="safe-zone safe-title" /></>}
      {selected && bounds && selected.visible && <div className="selection-box" style={{ left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height, transform: `rotate(${bounds.rotation}deg)` }} onPointerDown={(event) => onPointerDown(event, selected)}>
        <i className="handle handle-nw" /><i className="handle handle-ne" /><i className="handle handle-sw" /><i className="handle handle-se" /><span className="selection-label">{selected.name}</span>
      </div>}
    </div>
    {draggingFiles && <div className="drop-overlay"><UploadGlyph /><b>Drop to import and add</b><span>Video, audio, images, and 3D models</span></div>}
  </div>;
}

function UploadGlyph() { return <svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M12 16V4m0 0L7 9m5-5 5 5M5 15v4h14v-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>; }
